import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import {
  useAssignSupportTicketMutation,
  useCreateSupportTechnicianMutation,
  useCreateSupportTicketMutation,
  useGetAssignableTechniciansQuery,
  useGetMyDevicesForSupportQuery,
  useGetSupportTicketByIdQuery,
  useGetSupportTicketsQuery,
  useGetSupportTicketTypesQuery,
  useUpdateSupportTicketMutation,
} from "../../redux/admin/supportTicketApi";
import { useGetUserProfileQuery } from "../../redux/admin/userApi";

const STATUS_OPTIONS = [
  { value: "open", label: "OPEN" },
  { value: "assigned", label: "ASSIGNED" },
  { value: "in_progress", label: "IN PROGRESS" },
  { value: "resolved", label: "RESOLVED" },
  { value: "closed", label: "CLOSED" },
];

const emptyForm = {
  type: "",
  deviceId: "",
  description: "",
  remarks: "",
  status: "open",
  expectedResolutionDate: "",
  resolutionDate: "",
  assigneeId: "",
  attachment: null,
};

const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

const getTicketAttachmentUrl = (ticket = {}) =>
  String(ticket?.attachmentUrl || ticket?.attachment?.url || ticket?.fileUrl || "").trim();

const isImageAttachment = (url = "") =>
  /\.(png|jpe?g|webp|gif|bmp|svg)(?:\?|#|$)/i.test(url) ||
  /res\.cloudinary\.com\/.+\/image\/upload\//i.test(url);

const getAttachmentName = (url = "") => {
  if (!url) return "";
  try {
    const pathname = new URL(url).pathname;
    const last = pathname.split("/").filter(Boolean).pop() || "Attachment";
    return decodeURIComponent(last.replace(/\.[a-z0-9]+$/i, "")) || "Attachment";
  } catch {
    return "Attachment";
  }
};

const SupportTickets = () => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const { data: profile } = useGetUserProfileQuery();
  const role = profile?.role || "";
  const profileId = String(profile?._id || profile?.id || "");

  const canAssign = ["sub_admin", "group_admin", "manager", "admin"].includes(role);
  const canCreateTech = ["sub_admin", "group_admin"].includes(role);
  const isCommandRole = ["admin", "manager", "hr", "office_emp"].includes(role);
  const canEditByRole = ["sub_admin", "group_admin", "manager", "admin"].includes(role);

  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("created_by");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTicketId, setEditingTicketId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [formErr, setFormErr] = useState("");
  const [formInfo, setFormInfo] = useState("");
  const [addTechOpen, setAddTechOpen] = useState(false);
  const [techName, setTechName] = useState("");
  const [techMobile, setTechMobile] = useState("");
  const [techPassword, setTechPassword] = useState("");
  const [addTechErr, setAddTechErr] = useState("");

  const { data, isLoading, isError, error, refetch } = useGetSupportTicketsQuery(
    { search, scope },
    { refetchOnFocus: true, refetchOnReconnect: true }
  );
  const tickets = data?.tickets || [];

  const { data: ticketTypes = [] } = useGetSupportTicketTypesQuery(undefined, {
    skip: !editorOpen,
  });
  const { data: devices = [] } = useGetMyDevicesForSupportQuery(undefined, {
    skip: !editorOpen,
  });
  const {
    data: assignees = [],
    isLoading: assigneesLoading,
    refetch: refetchAssignees,
  } = useGetAssignableTechniciansQuery(undefined, {
    skip: !editorOpen || !canAssign,
  });
  const { data: ticketDetail, isFetching: ticketLoading } = useGetSupportTicketByIdQuery(
    { ticketId: editingTicketId, scope },
    { skip: !editorOpen || !editingTicketId }
  );

  const [createSupportTicket, { isLoading: creating }] = useCreateSupportTicketMutation();
  const [updateSupportTicket, { isLoading: updating }] = useUpdateSupportTicketMutation();
  const [assignSupportTicket, { isLoading: assigning }] = useAssignSupportTicketMutation();
  const [createSupportTechnician, { isLoading: creatingTech }] =
    useCreateSupportTechnicianMutation();

  const assigneeOptions = useMemo(() => {
    return (assignees || []).map((user) => {
      const isSelf = String(user?._id || "") === profileId;
      const roleLabel = isSelf ? "Assign To Me" : (user?.role || "user").replaceAll("_", " ");
      return {
        value: user._id,
        label: `${user.username || "Unknown"}${roleLabel ? ` (${roleLabel})` : ""}`,
      };
    });
  }, [assignees, profileId]);

  const selectedDeviceOption = useMemo(
    () => devices.find((device) => String(device._id) === String(form.deviceId)) || null,
    [devices, form.deviceId]
  );

  const canEditTicketRow = useMemo(
    () => (ticket) => {
      const creatorId = String(ticket?.createdBy?._id || ticket?.createdBy || "");
      const assignedId = String(ticket?.assignedTo?._id || ticket?.assignedTo || "");
      return canEditByRole || creatorId === profileId || assignedId === profileId;
    },
    [canEditByRole, profileId]
  );

  const canChangeTicketStatus = useMemo(
    () => (ticket) => {
      const creatorId = String(ticket?.createdBy?._id || ticket?.createdBy || "");
      const assignedId = String(ticket?.assignedTo?._id || ticket?.assignedTo || "");
      return isCommandRole || creatorId === profileId || assignedId === profileId;
    },
    [isCommandRole, profileId]
  );

  useEffect(() => {
    if (!editorOpen) return;

    if (!editingTicketId) {
      setForm(emptyForm);
      return;
    }

    if (!ticketDetail) return;

    setForm({
      type: ticketDetail.type || "",
      deviceId: ticketDetail.device?._id || ticketDetail.device || "",
      description: ticketDetail.description || "",
      remarks: ticketDetail.remarks || "",
      status: ticketDetail.status || "open",
      expectedResolutionDate: toDateInput(ticketDetail.expectedResolutionDate),
      resolutionDate: toDateInput(ticketDetail.resolutionDate),
      assigneeId: ticketDetail.assignedTo?._id || ticketDetail.assignedTo || "",
      attachment: null,
    });
    setFormErr("");
  }, [editorOpen, editingTicketId, ticketDetail]);

  const openCreate = () => {
    setEditingTicketId("");
    setForm(emptyForm);
    setFormErr("");
    setEditorOpen(true);
  };

  const openEdit = (ticketId) => {
    setEditingTicketId(ticketId);
    setFormErr("");
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingTicketId("");
    setForm(emptyForm);
    setFormErr("");
    setFormInfo("");
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleOpenAddTech = () => {
    setAddTechErr("");
    setTechName("");
    setTechMobile("");
    setTechPassword("");
    setAddTechOpen(true);
  };

  const handleCreateTech = async () => {
    try {
      setAddTechErr("");
      if (techName.trim().length < 2) {
        setAddTechErr("Technician name required");
        return;
      }
      if (techMobile.trim().length < 8) {
        setAddTechErr("Valid mobile number required");
        return;
      }

      const res = await createSupportTechnician({
        username: techName.trim(),
        mobileNo: techMobile.trim(),
        password: techPassword.trim() || undefined,
      }).unwrap();

      setAddTechOpen(false);
      await refetchAssignees();
      const createdId = res?.technician?._id;
      if (createdId) {
        setForm((prev) => ({ ...prev, assigneeId: createdId }));
      }
    } catch (e) {
      setAddTechErr(e?.data?.message || "Technician create failed");
    }
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append("type", form.type);
    fd.append("deviceId", form.deviceId);
    fd.append("description", form.description.trim());
    fd.append("remarks", form.remarks.trim());
    fd.append("status", form.status);
    fd.append("expectedResolutionDate", form.expectedResolutionDate || "");
    fd.append("resolutionDate", form.resolutionDate || "");
    if (form.attachment) {
      fd.append("attachment", form.attachment);
    }
    return fd;
  };

  const handleSave = async () => {
    try {
      setFormErr("");
      setFormInfo("");
      if (!form.type) return setFormErr("Select ticket type");
      if (!form.deviceId) return setFormErr("Select device");
      if (form.description.trim().length < 5) {
        return setFormErr("Description minimum 5 characters hona chahiye");
      }

      const assigneeChanged =
        editingTicketId &&
        String(form.assigneeId || "") !== String(ticketDetail?.assignedTo?._id || ticketDetail?.assignedTo || "");

      let savedTicketId = editingTicketId;

      if (!editingTicketId) {
        const created = await createSupportTicket(buildFormData()).unwrap();
        savedTicketId = created?.ticket?._id || "";
      } else {
        await updateSupportTicket({ ticketId: editingTicketId, formData: buildFormData() }).unwrap();
      }

      if (canAssign && savedTicketId && form.assigneeId && (!editingTicketId || assigneeChanged)) {
        try {
          await assignSupportTicket({ ticketId: savedTicketId, technicianId: form.assigneeId }).unwrap();
        } catch (assignError) {
          setFormInfo(
            `Ticket save ho gaya, but assignment update nahi hua: ${
              assignError?.data?.message || "Assign failed"
            }`
          );
          refetch();
          return;
        }
      }

      closeEditor();
      refetch();
    } catch (e) {
      setFormErr(e?.data?.message || "Ticket save failed");
    }
  };

  const isSaving = creating || updating || assigning;
  const activeTicket = editingTicketId ? ticketDetail : null;
  const activeAttachmentUrl = getTicketAttachmentUrl(activeTicket);
  const activeAttachmentIsImage = isImageAttachment(activeAttachmentUrl);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", lg: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: 30, md: 36 } }}>
          Support Tickets
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ p: 0.5, borderRadius: 2, bgcolor: "#f5f7fb", minWidth: 260 }}
          >
            <Button
              variant={scope === "created_by" ? "contained" : "text"}
              onClick={() => setScope("created_by")}
              sx={{ flex: 1, fontWeight: 800, borderRadius: 2 }}
            >
              Created By
            </Button>
            <Button
              variant={scope === "assigned_to_me" ? "contained" : "text"}
              onClick={() => setScope("assigned_to_me")}
              sx={{ flex: 1, fontWeight: 800, borderRadius: 2 }}
            >
              Assign To Me
            </Button>
          </Stack>

          <Button
            variant="contained"
            onClick={openCreate}
            startIcon={<AddIcon />}
            sx={{ px: 2.5, borderRadius: 2, fontWeight: 800, whiteSpace: "nowrap" }}
          >
            Add Support Ticket
          </Button>

          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 240 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon color="error" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Typography sx={{ fontWeight: 800 }}>
            Showing {tickets.length} ticket{tickets.length === 1 ? "" : "s"}
          </Typography>

          <IconButton onClick={refetch}>
            <RefreshIcon />
          </IconButton>
        </Stack>

        {isLoading ? (
          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error?.data?.message || "Failed to load tickets"}</Alert>
          </Box>
        ) : tickets.length === 0 ? (
          <Box sx={{ py: 8, px: 2, textAlign: "center" }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800 }}>No support tickets found</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Is scope me abhi koi ticket nahi hai.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Device Name</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Created By</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 800, minWidth: 220 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Issue Date</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Assigned To</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Remarks</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Expected Date</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Resolution Date</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Attachment</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tickets.map((ticket) => {
                  const attachmentUrl = getTicketAttachmentUrl(ticket);
                  const imageAttachment = isImageAttachment(attachmentUrl);

                  return (
                    <TableRow hover key={ticket._id}>
                      <TableCell>{ticket?.device?.name || ticket?.deviceSnapshot?.name || "-"}</TableCell>
                      <TableCell>
                        {ticket?.createdBy?.username || "-"}
                        {ticket?.createdBy?.role ? ` (${ticket.createdBy.role})` : ""}
                      </TableCell>
                      <TableCell>{ticket.type || "-"}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                          {ticket.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(ticket.status || "open").replaceAll("_", " ").toUpperCase()}
                          size="small"
                          color={
                            ticket.status === "resolved"
                              ? "success"
                              : ticket.status === "closed"
                                ? "default"
                                : ticket.status === "assigned" || ticket.status === "in_progress"
                                  ? "warning"
                                  : "error"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatDate(ticket.createdAt, true)}</TableCell>
                      <TableCell>
                        {ticket?.assignedTo?.username || "-"}
                        {ticket?.assignedTo?.role ? ` (${ticket.assignedTo.role})` : ""}
                      </TableCell>
                      <TableCell>{ticket.remarks || "-"}</TableCell>
                      <TableCell>{formatDate(ticket.expectedResolutionDate)}</TableCell>
                      <TableCell>{formatDate(ticket.resolutionDate)}</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        {attachmentUrl ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            {imageAttachment ? (
                              <Box
                                component="img"
                                src={attachmentUrl}
                                alt="Ticket attachment"
                                loading="lazy"
                                sx={{
                                  width: 42,
                                  height: 42,
                                  borderRadius: 1.25,
                                  objectFit: "cover",
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              />
                            ) : (
                              <InsertDriveFileIcon color="primary" fontSize="small" />
                            )}
                            <Button
                              component="a"
                              href={attachmentUrl}
                              target="_blank"
                              rel="noreferrer"
                              size="small"
                              startIcon={<OpenInNewIcon fontSize="small" />}
                              sx={{ whiteSpace: "nowrap", fontWeight: 800 }}
                            >
                              View
                            </Button>
                          </Stack>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {canEditTicketRow(ticket) ? (
                          <IconButton color="primary" onClick={() => openEdit(ticket._id)}>
                            <EditIcon />
                          </IconButton>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={editorOpen}
        onClose={closeEditor}
        fullWidth
        maxWidth="md"
        fullScreen={isSm}
        PaperProps={{ sx: { borderRadius: isSm ? 0 : 3 } }}
      >
        <DialogTitle sx={{ px: 3, py: 2.25, bgcolor: "#272c35", color: "#fff" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography sx={{ fontSize: 24, fontWeight: 900 }}>
                {editingTicketId ? "Edit Support Ticket" : "Add Support Ticket"}
              </Typography>
              {activeTicket ? (
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  {activeTicket?.createdBy?.username || "-"}{activeTicket?.createdBy?.role ? ` (${activeTicket.createdBy.role})` : ""}
                </Typography>
              ) : null}
            </Box>
            <IconButton onClick={closeEditor} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {ticketLoading ? (
            <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              {formErr ? <Alert severity="error">{formErr}</Alert> : null}
              {formInfo ? <Alert severity="warning">{formInfo}</Alert> : null}

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Type</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.type}
                      onChange={(e) => handleFormChange("type", e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select Type</em>
                      </MenuItem>
                      {ticketTypes.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Device</Typography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={devices}
                    value={selectedDeviceOption}
                    onChange={(_, value) => handleFormChange("deviceId", value?._id || "")}
                    getOptionLabel={(option) =>
                      option?.name
                        ? `${option.name}${option?.imei ? ` (${option.imei})` : ""}`
                        : option?.imei || option?._id || ""
                    }
                    isOptionEqualToValue={(option, value) => String(option?._id) === String(value?._id)}
                    ListboxProps={{
                      style: {
                        maxHeight: 260,
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select device"
                      />
                    )}
                    noOptionsText="No devices found"
                  />
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Description</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={form.description}
                    onChange={(e) => handleFormChange("description", e.target.value)}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Remarks</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={form.remarks}
                    onChange={(e) => handleFormChange("remarks", e.target.value)}
                  />
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Assign To</Typography>
                  <FormControl fullWidth size="small" disabled={!canAssign || assigneesLoading}>
                    <Select
                      value={form.assigneeId}
                      onChange={(e) => handleFormChange("assigneeId", e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select User</em>
                      </MenuItem>
                      {assigneeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!assigneesLoading && canAssign && assigneeOptions.length === 0 ? (
                    <Alert
                      severity="info"
                      sx={{ mt: 1 }}
                      action={
                        canCreateTech ? (
                          <Button size="small" onClick={handleOpenAddTech} startIcon={<PersonAddAltIcon />}>
                            Add Technician
                          </Button>
                        ) : null
                      }
                    >
                      Koi technician nahi mila. Abhi aap self assign kar sakte ho ya naya technician bana sakte ho.
                    </Alert>
                  ) : null}
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Status</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.status}
                      onChange={(e) => handleFormChange("status", e.target.value)}
                      disabled={editingTicketId ? !canChangeTicketStatus(activeTicket || {}) : false}
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Resolution Date</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={form.resolutionDate}
                    onChange={(e) => handleFormChange("resolutionDate", e.target.value)}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Expected Resolution Date</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={form.expectedResolutionDate}
                    onChange={(e) => handleFormChange("expectedResolutionDate", e.target.value)}
                  />
                </Box>
              </Stack>

              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
	                  Choose File
	                  <input
	                    hidden
	                    type="file"
	                    onChange={(e) => handleFormChange("attachment", e.target.files?.[0] || null)}
	                  />
	                </Button>
	                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
	                  {form.attachment?.name || (activeAttachmentUrl ? getAttachmentName(activeAttachmentUrl) : "No file selected")}
	                </Typography>
	                {activeAttachmentUrl ? (
	                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 1.25 }}>
	                    {activeAttachmentIsImage ? (
	                      <Box
	                        component="img"
	                        src={activeAttachmentUrl}
	                        alt="Current ticket attachment"
	                        loading="lazy"
	                        sx={{
	                          width: 56,
	                          height: 56,
	                          borderRadius: 1.5,
	                          objectFit: "cover",
	                          border: "1px solid",
	                          borderColor: "divider",
	                        }}
	                      />
	                    ) : (
	                      <InsertDriveFileIcon color="primary" />
	                    )}
	                    <Button
	                      component="a"
	                      href={activeAttachmentUrl}
	                      target="_blank"
	                      rel="noreferrer"
	                      size="small"
	                      startIcon={<OpenInNewIcon fontSize="small" />}
	                      sx={{ fontWeight: 800 }}
	                    >
	                      View current attachment
	                    </Button>
	                  </Stack>
	                ) : null}
	              </Box>

              {activeTicket?.assignedTo?.username ? (
                <Alert icon={<AssignmentIndIcon />} severity="info">
                  Current assignee: {activeTicket.assignedTo.username} ({activeTicket.assignedTo.role || "user"})
                </Alert>
              ) : null}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          {canCreateTech && editorOpen ? (
            <Button onClick={handleOpenAddTech} startIcon={<PersonAddAltIcon />}>
              Add Technician
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeEditor}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={isSaving || ticketLoading}>
            {isSaving ? "Saving..." : "SAVE"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addTechOpen} onClose={() => setAddTechOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontWeight: 900 }}>Add Technician</Typography>
            <IconButton onClick={() => setAddTechOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={1.2}>
            {addTechErr ? <Alert severity="error">{addTechErr}</Alert> : null}

            <TextField
              size="small"
              label="Name"
              value={techName}
              onChange={(e) => setTechName(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Mobile"
              value={techMobile}
              onChange={(e) => setTechMobile(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              type="password"
              label="Password (optional)"
              value={techPassword}
              onChange={(e) => setTechPassword(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddTechOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateTech} variant="contained" disabled={creatingTech}>
            {creatingTech ? "Creating..." : "CREATE"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportTickets;
